import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ModalDialog,
  SpinnerOverlayView,
  TextBlock,
} from '@/components/Elements';
import { FormInput } from '@/components/Form';
import { API_STATUS } from '@/constants';
import { ContractRenewalDialog } from '@/features/contract';
import { AccessExpiredDialog, LicenseInvalidDialog } from '@/features/expired';
import { useTranslationInfo } from '@/hooks/useTranslationInfo';
import { convertMillisToMinutes } from '@/utils/date';

import { PLAN_INFO_STATE } from '../hooks/usePlan';
import { usePlanDialog } from '../hooks/usePlanDialog';

import styles from './PlanDialog.module.scss';
import { SerialLinkDialog } from './SerialLinkDialog';

/**
 * プランダイアログ プロパティ
 */
type PlanDialog = {
  // ダイアログ表示/非表示(true=表示)
  isOpen: boolean;
  // ダイアログを閉じる
  onClose: () => void;
};

/**
 * お試しプランダイアログ プロパティ
 */
type FreePlanDialog = {
  // 利用可能残時間
  remainingTime: number;
  // [ライセンス購入]ボタンクリック処理
  onClickPurchaseButton: () => void;
  // [シリアル連携]リンクの表示/非表示
  isShownSerialLink: boolean;
};

/**
 * プラン確認ダイアログ プロパティ
 */
type PurchasedPlanDialog = {
  // ダイアログ内の説明文
  text: string;
  // シリアル
  serial: string;
  // [アカウント情報]ボタンクリック処理
  onClickAccountButton: () => void;
  // [アカウント情報]ボタンを非表示にするか否か
  isHiddenAccountButton: boolean;
  // ライセンス有効期限
  licenseExpDate: string | undefined;
  // [シリアル連携]リンクの表示/非表示
  isShownSerialLink: boolean;
};

/**
 * シリアル連携リンク プロパティ
 */
type SerialLink = {
  // リンク名
  linkName: string;
  // シリアル連携リンククリック処理
  onClickSerialLink: () => void;
  // リンクに付与するGAタグ名
  linkTagName: string;
};

/**
 * シリアル連携リンク
 */
const SerialLink = React.memo(
  ({ linkName, onClickSerialLink, linkTagName }: SerialLink) => (
    <div className={styles['serial-link']}>
      <button
        type="button"
        className={`${styles['serial-link-button']} ${linkTagName}`}
        onClick={onClickSerialLink}
      >
        <span className={styles['serial-link-text']}>{linkName}</span>
        <span className={styles['forward-icon']} />
      </button>
    </div>
  ),
);

/**
 * お試しプランダイアログ
 */
const FreePlanDialog = React.memo(
  ({
    isOpen,
    onClose,
    remainingTime,
    onClickPurchaseButton,
    onClickSerialLink,
    linkName,
    linkTagName,
    isShownSerialLink,
  }: PlanDialog & FreePlanDialog & SerialLink) => {
    const { t } = useTranslation();

    return (
      <ModalDialog
        isOpen={isOpen}
        isHiddenCloseButton={false}
        onClickClose={onClose}
        title={t('plan.お試しプラン').toString()}
      >
        <div className={styles['plan-container']}>
          <div className={styles['remaining-time']}>
            {t('plan.残り時間{num}分', {
              num: convertMillisToMinutes(remainingTime),
            })}
          </div>

          <div className={styles['trial-plan-notice']}>
            <p>{t('plan.毎月1日にリセットされます。')}</p>
            <p>
              {t(
                'plan.月30分までご利用いただけます。ライセンスを購入するか、コードを入力すると、無制限でご利用いただけます。',
              )}
            </p>
          </div>

          <div className={styles['purchase-license']}>
            <Button
              type="button"
              title={t('plan.ライセンス購入')}
              onClick={onClickPurchaseButton}
            />
          </div>

          {isShownSerialLink && (
            <SerialLink
              onClickSerialLink={onClickSerialLink}
              linkName={linkName}
              linkTagName={linkTagName}
            />
          )}
        </div>
      </ModalDialog>
    );
  },
);

/**
 * サブスク購入済みプランダイアログ
 */
const PurchasedPlanDialog = React.memo(
  ({
    isOpen,
    onClose,
    text,
    serial,
    onClickAccountButton,
    isHiddenAccountButton,
    licenseExpDate,
    onClickSerialLink,
    linkName,
    linkTagName,
    isShownSerialLink,
  }: PlanDialog & PurchasedPlanDialog & SerialLink) => {
    const { t } = useTranslation();

    return (
      <ModalDialog
        isOpen={isOpen}
        isHiddenCloseButton={false}
        onClickClose={onClose}
        title={t('plan.プラン').toString()}
      >
        <div className={styles['plan-container']}>
          <div>
            <div className={styles['remaining-time']}>{text}</div>
            {licenseExpDate && (
              <div className={styles['license-exp']}>
                {t('plan.有効期限')}
                {licenseExpDate}
              </div>
            )}
          </div>

          <div className={styles['serial-container']}>
            <div className={styles.serial}>
              <TextBlock text={t('plan.シリアルコード')} />
              <FormInput
                type="text"
                id="serial"
                name="serial"
                disabled
                defaultValue={serial}
              />
            </div>

            <div className={styles['account-info']}>
              {!isHiddenAccountButton && (
                <Button
                  type="button"
                  title={t('plan.アカウント情報')}
                  onClick={onClickAccountButton}
                />
              )}
            </div>

            {isShownSerialLink && (
              <SerialLink
                onClickSerialLink={onClickSerialLink}
                linkName={linkName}
                linkTagName={linkTagName}
              />
            )}
          </div>
        </div>
      </ModalDialog>
    );
  },
);

/**
 * プランダイアログ
 * ※利用中のプランによってダイアログ内の内容を出し分ける
 */
export const PlanDialog = React.memo(({ isOpen, onClose }: PlanDialog) => {
  const {
    licenseInfoStatus,
    licenseExpDate,
    onClickPurchaseButton,
    serial,
    onClickAccountButton,
    isHiddenAccountButton,
    needsLoginAgain,
    planInfoResult,
    shouldShowSerialLink,
    isOpenSerialLinkDialog,
    onClickSerialLink,
    onClickSerialLinkDialogClose,
    handleSerialLinkSuccess,
    planDialogText,
    planDialogContent,
  } = usePlanDialog({ onFailedFun: onClose });
  const { isFreePlan, remainingTime } = useTranslationInfo();
  const { t } = useTranslation();

  if (needsLoginAgain) {
    return (
      // 契約状態更新後の自動ログインダイアログを表示
      // 必ず表示するのでisOpenにtrueを直指定
      <ContractRenewalDialog isOpen />
    );
  }

  return (
    <>
      {/* プログレスサークルを表示 */}
      <SpinnerOverlayView isOpen={licenseInfoStatus === API_STATUS.LOADING} />
      {/* ライセンス不正ダイアログを表示 */}
      <LicenseInvalidDialog
        isOpen={planInfoResult === PLAN_INFO_STATE.LICENSE_EXP}
      />
      {/* アクセス期限切れダイアログを表示 */}
      <AccessExpiredDialog
        isOpen={planInfoResult === PLAN_INFO_STATE.ACCESS_EXP}
      />
      {licenseInfoStatus !== API_STATUS.LOADING &&
        planInfoResult === PLAN_INFO_STATE.OK &&
        (isFreePlan ? (
          // お試しプランダイアログを表示
          <FreePlanDialog
            isOpen={isOpen}
            onClose={onClose}
            remainingTime={remainingTime}
            onClickPurchaseButton={onClickPurchaseButton}
            onClickSerialLink={onClickSerialLink}
            linkName={t(`planDialog.${planDialogContent.linkName}`).toString()}
            linkTagName="dialog-24"
            isShownSerialLink={shouldShowSerialLink}
          />
        ) : (
          // サブスク購入済みプランダイアログを表示
          <PurchasedPlanDialog
            isOpen={isOpen}
            onClose={onClose}
            text={t(`planDialog.${planDialogText}`).toString()}
            serial={serial}
            onClickAccountButton={onClickAccountButton}
            isHiddenAccountButton={isHiddenAccountButton}
            licenseExpDate={licenseExpDate}
            onClickSerialLink={onClickSerialLink}
            linkName={t(`planDialog.${planDialogContent.linkName}`).toString()}
            linkTagName="dialog-25"
            isShownSerialLink={shouldShowSerialLink}
          />
        ))}

      {/* シリアル連携ダイアログを表示 */}
      <SerialLinkDialog
        isOpen={isOpenSerialLinkDialog}
        onClose={onClickSerialLinkDialogClose}
        onSerialLinkSuccessFun={handleSerialLinkSuccess}
      />
    </>
  );
});
